import { WishlistUpdateEvent } from '@theme/events';

/**
 * @typedef {Object} WishlistItem
 * @property {string} id - Product id.
 * @property {string} variantId - The variant that was selected on the product card when added;
 *   the wishlist drawer's "Add to cart" button adds this exact variant, not a re-resolved one.
 * @property {string} handle
 * @property {string} url
 * @property {string} title
 * @property {string} image
 * @property {string} price - Pre-formatted money string (server-rendered, so it already
 *   respects the shop's currency/locale — this module never formats money itself).
 * @property {string} [comparePrice] - Pre-formatted compare-at price, when on sale.
 * @property {boolean} [requiresPersonalization] - From snippets/personalization-required.liquid;
 *   the drawer sends these to the product page instead of adding them. Only `true` is trusted:
 *   the drawer re-checks anything else against the product's current tags before adding.
 */

const STORAGE_KEY = 'evry-wishlist';

/**
 * Reads the wishlist from localStorage. Swallows parse/storage errors (private browsing,
 * corrupted value, quota) and returns an empty list rather than throwing — a broken wishlist
 * read must never break the page it's read from.
 * @returns {WishlistItem[]}
 */
export function getWishlist() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * @param {string|number} id
 * @returns {boolean}
 */
export function isWishlisted(id) {
  const idStr = String(id);
  return getWishlist().some((item) => String(item.id) === idStr);
}

/**
 * @param {WishlistItem[]} items
 * @param {'add'|'remove'} action
 * @param {string|number} changedId
 */
function persist(items, action, changedId) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // Quota exceeded or storage disabled — the in-memory event still fires so the UI
    // reflects this session's state, it just won't survive a reload.
  }

  document.dispatchEvent(
    new WishlistUpdateEvent({
      items,
      count: items.length,
      changedId,
      action,
    })
  );
}

/**
 * Adds a product to the wishlist. No-ops (does not duplicate or re-fire) if already present.
 * @param {WishlistItem} item
 */
export function addToWishlist(item) {
  const items = getWishlist();
  if (items.some((existing) => String(existing.id) === String(item.id))) return;

  items.unshift(item);
  persist(items, 'add', item.id);
}

/**
 * @param {string|number} id
 */
export function removeFromWishlist(id) {
  const idStr = String(id);
  const items = getWishlist();
  const next = items.filter((item) => String(item.id) !== idStr);
  if (next.length === items.length) return;

  persist(next, 'remove', id);
}

/**
 * @param {string|number} id
 * @param {WishlistItem} item - Full item, used only when the id isn't already wishlisted.
 * @returns {boolean} The new wishlisted state.
 */
export function toggleWishlist(id, item) {
  if (isWishlisted(id)) {
    removeFromWishlist(id);
    return false;
  }
  addToWishlist(item);
  return true;
}

/**
 * Fires a hydration WishlistUpdateEvent from the current localStorage state, without writing
 * anything. Call once per page load so every listener (badge, drawer, card hearts) initializes
 * from the same source instead of each reading localStorage independently.
 */
export function hydrateWishlist() {
  const items = getWishlist();
  document.dispatchEvent(
    new WishlistUpdateEvent({
      items,
      count: items.length,
      action: 'hydrate',
    })
  );
}

document.addEventListener('DOMContentLoaded', hydrateWishlist);
if (document.readyState !== 'loading') hydrateWishlist();
